import Statistic from '@components/design-system/data-display/statistic';
import useSmsCode from '@hook/useSmsCode';
import { Button, Center, Group, Stack, Text, TextInput } from '@mantine/core';
import { useForm, yupResolver } from '@mantine/form';
import moment, { DurationInputArg1 } from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import starkString from 'starkstring';
import * as Yup from 'yup';
import Errors from '../../../../components/design-system/general/error';
import businessConfig from '../../../../config/business';
import { verifyOtp } from '../../../../reducers/auth';
import { sendOtpForUser } from '../../../../reducers/otp';
import { AppDispatch, RootState } from '../../../../stores';

const { Countdown } = Statistic;

const CounterComponent = () => {
  const RETRY_TIME: DurationInputArg1 = { minutes: 2 };
  const dispatch: AppDispatch = useDispatch();
  const [deadline, setDeadline] = useState(moment().add(RETRY_TIME));

  const { phoneNumber } = useSelector((state: RootState) => state.otp);

  const retryOtp = () => {
    if (!phoneNumber) return;

    setDeadline(moment().add(RETRY_TIME));
    dispatch(sendOtpForUser({ phoneNumber }));
  };

  const finishedCountDownComponent = (
    <Center>
      <Stack gap="sm">
        <Text color="brand.2">کد تایید را دریافت نکرده‌اید؟</Text>
        <Button onClick={retryOtp} size="xs">
          ارسال دوباره
        </Button>
      </Stack>
    </Center>
  );

  const counterComponent = (
    <Text color="brand.2" size="xs" ta="center">
      <Countdown deadline={deadline} format="mm:ss">
        {finishedCountDownComponent}
      </Countdown>
    </Text>
  );

  return <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>{counterComponent}</div>;
};

interface ValuesType {
  otp: string;
  firstName?: string;
  lastName?: string;
}

const loginSchema = Yup.object().shape({
  otp: Yup.string()
    .length(businessConfig.otpLength, `کد اعتبار سنجی ${businessConfig.otpLength} رقمی است`)
    .required('وارد کردن کد الزامی است'),
  firstName: Yup.string().optional(),
  lastName: Yup.string().optional(),
});

const registerSchema = loginSchema.shape({
  firstName: Yup.string().required('وارد کردن نام الزامی است'),
  lastName: Yup.string().required('وارد کردن نام خانوادگی الزامی است'),
});

interface OTPDialogComponentProps {
  setIsOpen: Function;
}

const OTPDialogComponent = ({ setIsOpen }: OTPDialogComponentProps) => {
  const dispatch: AppDispatch = useDispatch();

  const { userId } = useSelector((state: RootState) => state.otp);
  const { isRegistered } = useSelector((state: RootState) => state.otp);
  const { loading, errors, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (user) setIsOpen(false);
  }, [user, setIsOpen]);

  const schema = isRegistered ? loginSchema : registerSchema;

  const form = useForm<ValuesType>({
    initialValues: {
      otp: '',
      firstName: undefined,
      lastName: undefined,
    },
    validate: yupResolver(schema),
  });

  const signUpFields = isRegistered ? null : (
    <>
      <TextInput label="نام" {...form.getInputProps('firstName')} />
      <TextInput label="نام خانوادگی" {...form.getInputProps('lastName')} />
    </>
  );

  const onFormSubmit = (values: ValuesType, thisUserId: string | undefined) => {
    const { otp, firstName, lastName } = values;

    const otpData = {
      otp: otp.toString(),
      userId: thisUserId || '',
      ...(isRegistered ? {} : { firstName, lastName }),
    };

    dispatch(verifyOtp(otpData));
  };

  // The code read from the SMS: filled in, and a returning customer is signed in without typing.
  useSmsCode((otp) => {
    form.setFieldValue('otp', otp);
    if (isRegistered && !loading) onFormSubmit({ ...form.values, otp }, userId);
  }, businessConfig.otpLength);

  return (
    <form onSubmit={form.onSubmit((values) => onFormSubmit(values, userId))}>
      <Stack gap="sm">
        <Group justify="center">
          <TextInput
            label="کد یکبار مصرف"
            description="لطفا کد یکبار مصرف را وارد کنید"
            radius="md"
            name="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            minLength={businessConfig.otpLength}
            maxLength={businessConfig.otpLength}
            {...form.getInputProps('otp')}
            onChange={(val) =>
              form
                .getInputProps('otp')
                .onChange(starkString(val.target.value).englishNumber().toString().replaceAll(' ', ''))
            }
            autoFocus
          />
        </Group>

        <CounterComponent />

        {signUpFields}

        {errors && <Errors errors={errors} />}

        <Button type="submit" disabled={!form.isValid()} loading={loading}>
          ورود
        </Button>
      </Stack>
    </form>
  );
};

export default OTPDialogComponent;
